
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import moment from 'moment';

moment.locale('it');

@Component
export default class DataTableHeader extends Vue {
  @Prop(String) readonly title!: string;

  @Prop([String, Array]) readonly name!: string | string[] | undefined;

  @Prop([String, Array]) readonly label!: string | string[] | undefined;

  @Prop(Array) readonly searchOptions!: {
    label: string;
    value: string;
    default?: boolean;
  }[] | undefined;

  @Prop(String) readonly currentSearchValue!: string | null | undefined;
  @Prop(Array) readonly currentSearchItems!: string[] | undefined;
  @Prop(String) readonly currentSearchLogic!: 'AND' | 'OR' | undefined;
  @Prop(String) readonly currentSearchExclude!: string | null | undefined;

  @Prop(Object) readonly sortOptions!: {
    sortBy?: string[];
    sortDesc?: boolean[] | string[];
  } | undefined;

  @Prop({ type: Boolean, default: false }) readonly noDateFilter!: boolean;

  @Prop(Object) readonly currentDates: {
    value: string | null;
    begin: Date | null | false;
    end: Date | null | false;
  } | undefined;

  // Dialog per la selezione dell'intervallo di date
  dialog = false
  dates: string[] = []
  begin: Date | null | false = null
  end: Date | null | false = null

  // Dialog per la ricerca testuale
  searchDialog = false
  searchValue: string | null = null
  selectedFilterSearchItems: string[] = [];
  filterSearchInput: string | null = null;
  searchLogic: 'AND' | 'OR' = 'OR'
  searchExclude: string | null = null

  // Campo data selezionato per modalità multipla
  selectedDateField: string | null = null;

  @Watch('dates')
  onDatesChange(val: string[]) {
    console.log(`${this.constructor.name}: onDatesChange()`); // eslint-disable-line no-console

    if (val.length === 0) {
      this.begin = null;
      this.end = null;
    } else if (val.length === 1) {
      this.begin = moment(val[0], 'YYYY-MM-DD').toDate();
      this.end = null;
    } else if (val.length === 2) {
      this.begin = val[1] > val[0] ? moment(val[0], 'YYYY-MM-DD').toDate() : moment(val[1], 'YYYY-MM-DD').toDate();
      this.end = val[1] > val[0] ? moment(val[1], 'YYYY-MM-DD').toDate() : moment(val[0], 'YYYY-MM-DD').toDate();
    }
  }

  @Watch('dialog')
  onDialogChange(val: boolean, oldVal: boolean) {
    console.log(`${this.constructor.name}: onDialogChange()`); // eslint-disable-line no-console

    if (oldVal === true && val === false) {
      this.$emit('restartCountdown');
    }
  }

  @Watch('searchDialog')
  onSearchDialogChange(val: boolean, oldVal: boolean) {
    console.log(`${this.constructor.name}: onSearchDialogChange()`); // eslint-disable-line no-console

    // Se il dialogo viene chiuso, riavvia il countdown
    if (oldVal === true && val === false) {
      this.$emit('restartCountdown');
    }
  }

  get isSortEnabled(): boolean {
    if (this.isMultiDateMode) {
      return this.isAnyDateFieldSorted;
    }

    const nameStr = this.name as string | undefined;
    if (nameStr === undefined || !this.sortOptions) return false;

    const sortOpts = this.sortOptions as {
      sortBy?: string[];
      sortDesc?: boolean[] | string[];
    };

    return Array.isArray(sortOpts.sortBy) && sortOpts.sortBy.includes(nameStr);
  }

  get sortColor(): string {
    if (this.isSortEnabled) {
      return 'orange';
    }
    return 'grey';
  }

  get sortIcon(): string | null {
    if (this.isMultiDateMode) {
      // In modalità multipla, mostra icona basata su qualsiasi campo ordinato
      const sortedField = this.dateFields.find((field) => {
        const state = this.getSortStateForField(field.value);
        return state.enabled;
      });

      if (sortedField) {
        const state = this.getSortStateForField(sortedField.value);
        return state.isDesc
          ? 'mdi-sort-numeric-descending-variant'
          : 'mdi-sort-numeric-ascending-variant';
      }
      return 'mdi-sort-numeric-variant';
    }

    const nameStr = this.name as string | undefined;
    if (nameStr === undefined || !this.sortOptions) return null;

    const sortOpts = this.sortOptions as {
      sortBy?: string[];
      sortDesc?: boolean[] | string[];
    };

    if (sortOpts.sortBy && sortOpts.sortBy.includes(nameStr)) {
      const index = sortOpts.sortBy.findIndex((n) => n === nameStr);
      if (index !== null && index !== undefined && index > -1) {
        return sortOpts.sortDesc
          && index in sortOpts.sortDesc
          && (sortOpts.sortDesc[index] === false || sortOpts.sortDesc[index] === 'false')
          ? 'mdi-sort-numeric-ascending-variant'
          : 'mdi-sort-numeric-descending-variant';
      }
    }
    return 'mdi-sort-numeric-variant';
  }

  get sortText(): string | null {
    if (this.isMultiDateMode) {
      const sortedField = this.dateFields.find((field) => {
        const state = this.getSortStateForField(field.value);
        return state.enabled;
      });

      if (sortedField) {
        const state = this.getSortStateForField(sortedField.value);
        return state.isDesc ? 'Ordine decrescente' : 'Ordine crescente';
      }
      return null;
    }

    if (!this.isSortEnabled) return null;

    const nameStr = this.name as string | undefined;
    if (nameStr === undefined || !this.sortOptions) return null;

    const sortOpts = this.sortOptions as {
      sortBy?: string[];
      sortDesc?: boolean[] | string[];
    };

    if (sortOpts.sortBy && sortOpts.sortBy.includes(nameStr)) {
      const index = sortOpts.sortBy.findIndex((n) => n === nameStr);
      if (index !== null && index !== undefined && index > -1) {
        return sortOpts.sortDesc
          && index in sortOpts.sortDesc
          && (sortOpts.sortDesc[index] === false || sortOpts.sortDesc[index] === 'false')
          ? 'Ordine crescente'
          : 'Ordine decrescente';
      }
    }

    return null;
  }

  get isFilterEnabled(): boolean {
    if (!this.currentDates) return false;

    if (this.isMultiDateMode) {
      // In modalità multipla, verifica se currentDates.value corrisponde a uno dei campi data
      const nameArray = this.name as string[];
      return nameArray.includes(this.currentDates.value || '')
        && this.currentDates.begin !== null
        && this.currentDates.end !== null;
    }

    const nameStr = this.name as string | undefined;
    if (nameStr === undefined) return false;

    return this.currentDates.value === nameStr
      && this.currentDates.begin !== null
      && this.currentDates.end !== null;
  }

  get filterColor(): string {
    if (!this.isFilterEnabled || !this.currentDates) return 'grey';

    return this.currentDates.begin === false && this.currentDates.end === false ? 'red' : 'orange';
  }

  get currentDateRangeText(): string | null {
    if (!this.currentDates) return null;

    if (this.isMultiDateMode) {
      // In modalità multipla, mostra l'etichetta del campo data attivo
      const activeField = this.dateFields.find((field) => field.value === this.currentDates?.value);
      if (!activeField) return null;

      const fieldLabel = activeField.label;

      if (this.currentDates.begin === false && this.currentDates.end === false) {
        return `${fieldLabel}: Senza data`;
      }

      if (this.currentDates.begin && this.currentDates.end) {
        const beginMoment = moment(this.currentDates.begin);
        const endMoment = moment(this.currentDates.end);
        const dateText = beginMoment.isSame(endMoment)
          ? beginMoment.format('L')
          : `Dal ${beginMoment.format('L')} al ${endMoment.format('L')}`;
        return `${fieldLabel}: ${dateText}`;
      }

      return null;
    }

    if (this.currentDates.begin && this.currentDates.end) {
      return moment(this.currentDates.begin).isSame(this.currentDates.end)
        ? `${moment(this.currentDates.begin).format('L')}`
        : `Dal ${moment(this.currentDates.begin).format('L')} al ${moment(this.currentDates.end).format('L')}`;
    }

    if (this.currentDates.begin === false && this.currentDates.end === false) {
      return 'Senza data';
    }

    return null;
  }

  get dateRangeText(): string | null {
    if (this.begin && !this.end) {
      return `${moment(this.begin).format('L')}`;
    }

    if (this.begin && this.end) {
      return moment(this.begin).isSame(this.end)
        ? `${moment(this.begin).format('L')}`
        : `Dal ${moment(this.begin).format('L')} al ${moment(this.end).format('L')}`;
    }

    return null;
  }

  get isResetEnabled(): boolean {
    if (!this.currentDates) return false;

    return this.currentDates.begin !== null && this.currentDates.end !== null;
  }

  get isApplyEnabled(): boolean {
    return Array.isArray(this.dates) && this.dates.length > 0;
  }

  get searchItems() {
    return this.searchOptions?.map((item) => ({
      name: item.label,
      id: item.value,
    })) ?? [];
  }

  // Computed properties per modalità date multiple
  get isMultiDateMode(): boolean {
    return Array.isArray(this.name) && this.name.length > 0;
  }

  get dateFields(): Array<{ value: string; label: string }> {
    if (!this.isMultiDateMode) {
      return [];
    }

    const names = this.name as string[];
    const labels = Array.isArray(this.label) ? this.label as string[] : [];

    return names.map((nameValue, index) => ({
      value: nameValue,
      label: labels[index] || nameValue,
    }));
  }

  currentSortOptionsForField(fieldName: string): {
    sortBy?: string[];
    sortDesc?: boolean[] | string[];
  } | undefined {
    if (!this.sortOptions) return undefined;

    // sortOptions è sempre un oggetto singolo (sia in modalità singola che multipla)
    // La verifica se il campo è in sortBy viene fatta in getSortStateForField
    return this.sortOptions;
  }

  currentDatesForField(fieldName: string): {
    value: string | null;
    begin: Date | null | false;
    end: Date | null | false;
  } | undefined {
    if (!this.currentDates) return undefined;

    // In modalità multipla, currentDates.value contiene il nome del campo data attivo
    // Se corrisponde a fieldName, restituisce currentDates
    if (this.currentDates.value === fieldName) {
      return this.currentDates;
    }

    return undefined;
  }

  getSortStateForField(fieldName: string): {
    enabled: boolean;
    isDesc: boolean;
  } {
    const sortOpts = this.currentSortOptionsForField(fieldName);
    if (!sortOpts || !Array.isArray(sortOpts.sortBy)) {
      return { enabled: false, isDesc: false };
    }

    const index = sortOpts.sortBy.indexOf(fieldName);
    if (index < 0) {
      return { enabled: false, isDesc: false };
    }

    const isDesc = sortOpts.sortDesc
      && index in sortOpts.sortDesc
      && sortOpts.sortDesc[index] !== false
      && sortOpts.sortDesc[index] !== 'false';

    return { enabled: true, isDesc: !!isDesc };
  }

  getFilterStateForField(fieldName: string): {
    enabled: boolean;
    isEmpty: boolean;
    begin: Date | null | false;
    end: Date | null | false;
  } {
    const dates = this.currentDatesForField(fieldName);
    if (!dates) {
      return {
        enabled: false,
        isEmpty: false,
        begin: null,
        end: null,
      };
    }

    const enabled = dates.value === fieldName
      && dates.begin !== null
      && dates.end !== null;

    const isEmpty = dates.begin === false && dates.end === false;

    return {
      enabled,
      isEmpty,
      begin: dates.begin,
      end: dates.end,
    };
  }

  get isAnyDateFieldFiltered(): boolean {
    return this.isFilterEnabled;
  }

  get isAnyDateFieldSorted(): boolean {
    if (!this.isMultiDateMode) {
      return this.isSortEnabled;
    }

    return this.dateFields.some((field) => {
      const state = this.getSortStateForField(field.value);
      return state.enabled;
    });
  }

  getDateFieldLabel(fieldName: string): string {
    if (this.isMultiDateMode) {
      const field = this.dateFields.find((f) => f.value === fieldName);
      return field ? field.label : fieldName;
    }
    // In modalità singola, usa label se specificato, altrimenti title come fallback
    return (this.label as string) || this.title || fieldName;
  }

  getSubheaderColorClass(fieldName: string): string {
    const state = this.getFilterStateForField(fieldName);
    if (!state.enabled) {
      return 'primary--text';
    }
    // Se è attivo il filtro "senza data", usa rosso, altrimenti arancione
    return state.isEmpty ? 'red--text' : 'orange--text';
  }

  onItemsFilterUpdate(items: string[]) {
    console.log(`${this.constructor.name}: onItemsFilterUpdate(): ${items}`); // eslint-disable-line no-console

    this.selectedFilterSearchItems = items;
  }

  onSort() {
    console.log(`${this.constructor.name}: onSort()`); // eslint-disable-line no-console

    const nameStr = this.name as string;
    this.$emit('sort', nameStr);
  }

  onSortForField(fieldName: string) {
    console.log(`${this.constructor.name}: onSortForField('${fieldName}')`); // eslint-disable-line no-console

    // Emetti il nome del campo data specifico (in modalità multipla fieldName è già il campo specifico)
    this.$emit('sort', fieldName);
  }

  onFilter() {
    console.log(`${this.constructor.name}: onFilter()`); // eslint-disable-line no-console

    if (this.currentDates === undefined) return;

    this.$emit('pauseCountdown');

    const nameStr = this.name as string;

    // Imposta l'eventuale range iniziale
    if (this.currentDates.value === nameStr && this.currentDates.begin && this.currentDates.end) {
      this.begin = this.currentDates.begin;
      this.end = this.currentDates.end;

      this.dates = [
        moment(this.begin).format('YYYY-MM-DD'),
        moment(this.end).format('YYYY-MM-DD'),
      ];
    } else {
      this.begin = null;
      this.end = null;

      this.dates = [];
    }

    this.selectedDateField = null;
    this.dialog = true;
  }

  onFilterForField(fieldName: string) {
    console.log(`${this.constructor.name}: onFilterForField('${fieldName}')`); // eslint-disable-line no-console

    if (this.currentDates === undefined) return;

    this.$emit('pauseCountdown');

    this.selectedDateField = fieldName;

    const dates = this.currentDatesForField(fieldName);

    // Imposta l'eventuale range iniziale
    if (dates && dates.value === fieldName && dates.begin && dates.end) {
      this.begin = dates.begin;
      this.end = dates.end;

      this.dates = [
        moment(this.begin).format('YYYY-MM-DD'),
        moment(this.end).format('YYYY-MM-DD'),
      ];
    } else {
      this.begin = null;
      this.end = null;

      this.dates = [];
    }

    this.dialog = true;
  }

  onEmptyForField(fieldName: string) {
    console.log(`${this.constructor.name}: onEmptyForField('${fieldName}')`); // eslint-disable-line no-console

    if (this.dialog) this.dialog = false;

    // Emetti il nome del campo data specifico (in modalità multipla fieldName è già il campo specifico)
    this.$emit('filter', fieldName, false, false);
  }

  onResetForField(fieldName: string) {
    console.log(`${this.constructor.name}: onResetForField('${fieldName}')`); // eslint-disable-line no-console

    if (this.dialog) this.dialog = false;
    if (this.searchDialog) this.searchDialog = false;

    // Emetti il nome del campo data specifico (in modalità multipla fieldName è già il campo specifico)
    this.$emit('filter', fieldName, null, null);
    this.$emit('search', {
      items: [], value: null, logic: null, exclude: null,
    });
  }

  get searchFilterColor(): string {
    return this.currentSearchValue ? 'orange' : 'grey';
  }

  get isSearchFilterEnabled(): boolean {
    return this.currentSearchValue !== undefined && this.currentSearchValue !== null;
  }

  get currentSearchFilterText(): string {
    return this.currentSearchValue || 'Nessun filtro';
  }

  removeSearchItem(searchItem: { id: string; name: string }) {
    console.log(`${this.constructor.name}: removeSearchItem() searchItem =`, searchItem); // eslint-disable-line no-console

    this.selectedFilterSearchItems = this.selectedFilterSearchItems
      .filter((itemId) => itemId !== searchItem.id);
  }

  onSearchFilter() {
    console.log(`${this.constructor.name}: onSearchFilter()`); // eslint-disable-line no-console

    this.$emit('pauseCountdown');

    // Imposta il valore di ricerca iniziale a quello memorizzato nei filtri (se presente)
    this.searchValue = this.currentSearchValue ?? '';
    this.searchLogic = this.currentSearchLogic === 'AND' ? 'AND' : 'OR';
    this.searchExclude = this.currentSearchExclude ?? null;

    // Se non ci sono campi di ricerca selezionati, inizializzo con quelli di default.
    if (!this.currentSearchItems || this.currentSearchItems.length === 0) {
      this.selectedFilterSearchItems = this.searchOptions?.filter((item) => item.default)
        .map((item) => (item.value)) ?? [];
    } else {
      // Altrimenti inizializzo con quelli memorizzati nei filtri.
      this.selectedFilterSearchItems = this.currentSearchItems;
    }

    this.searchDialog = true;
  }

  onSearchApply() {
    console.log(`${this.constructor.name}: onSearchApply()`); // eslint-disable-line no-console

    if (this.searchDialog) this.searchDialog = false;

    this.$emit('search', {
      items: this.selectedFilterSearchItems,
      value: this.searchValue,
      logic: this.searchLogic,
      exclude: this.searchExclude || null,
    });
  }

  onReset() {
    console.log(`${this.constructor.name}: onReset()`); // eslint-disable-line no-console

    if (this.dialog) this.dialog = false;
    if (this.searchDialog) this.searchDialog = false;

    if (this.isMultiDateMode && this.selectedDateField) {
      // In modalità multipla, usa il campo data selezionato
      this.$emit('filter', this.selectedDateField, null, null);
      this.selectedDateField = null;
    } else {
      const nameStr = this.name as string;
      this.$emit('filter', nameStr, null, null);
    }
    this.$emit('search', {
      items: [], value: null, logic: null, exclude: null,
    });
  }

  onApply() {
    console.log(`${this.constructor.name}: onApply()`); // eslint-disable-line no-console

    if (this.dialog) this.dialog = false;

    if (this.isMultiDateMode && this.selectedDateField) {
      // In modalità multipla, usa il campo data selezionato
      this.$emit('filter', this.selectedDateField, this.begin, this.end);
      this.selectedDateField = null;
    } else {
      const nameStr = this.name as string;
      this.$emit('filter', nameStr, this.begin, this.end);
    }
  }

  onEmpty() {
    console.log(`${this.constructor.name}: onEmpty()`); // eslint-disable-line no-console

    if (this.dialog) this.dialog = false;

    const nameStr = this.name as string;
    this.$emit('filter', nameStr, false, false);
  }

  selectCurrentYear() {
    const currentYear = moment().year();
    this.begin = moment(`${currentYear}-01-01`).toDate();
    this.end = moment(`${currentYear}-12-31`).toDate();
    this.dates = [
      moment(this.begin).format('YYYY-MM-DD'),
      moment(this.end).format('YYYY-MM-DD'),
    ];
  }

  selectPreviousYear() {
    const previousYear = moment().year() - 1;
    this.begin = moment(`${previousYear}-01-01`).toDate();
    this.end = moment(`${previousYear}-12-31`).toDate();
    this.dates = [
      moment(this.begin).format('YYYY-MM-DD'),
      moment(this.end).format('YYYY-MM-DD'),
    ];
  }
}
